import debug from "debug";
import { action, computed, observable, ObservableMap, toJS } from "mobx";
import { isEmpty } from "lodash";
import locale from "@bonliva-crm/locale";
import {
  CountryFragment,
  ExpertiseFragment,
  FetchStaticData,
  RoleFragment,
  Team
} from "@graphql";
import { BaseStore } from "../BaseStore";
import { bookingInvoiceSpecialCodes } from "@bonliva-crm/shared";

const log = debug("app:store:CacheStore");
const uniqFlatten = <T>(v: T[][]) => v.selectMany((x) => x).unique();

/**
 * This store caches some important data on app start that we can reuse across the app
 */
export class CacheStore extends BaseStore {
  /** All the teams present in the system */
  @observable teams: Team[] = [];
  @observable countries: string[] = [];
  /** All countries in the whole world */
  allCountriesWorldwide: CountryFragment[] = [];
  @observable regions: ObservableMap<string, string[]> = observable.map();
  @observable regionsWithBonlivaCare: ObservableMap<string, string[]> = observable.map();
  areasOfExp: ExpertiseFragment[] = [];
  customerGroups: string[] = [];
  competitors: string[] = [];
  languages: string[] = [];
  mainLanguages = mainLanguages;
  roles: RoleFragment[] = [];
  systems: string[] = ["system1"];
  machines: string[] = [];
  titles: string[] = [];
  nationalProcurementTravelRegions: string[] = [];
  /** Mirrors the `blockBookingCreationForBemloCustomers` config key (CRMDEV-7351). */
  blockBookingCreationForBemloCustomers = false;

  specialInvoices = bookingInvoiceSpecialCodes;

  benefitTaxFactorsPerYear: Record<number, BenefitTaxFactors> = {
    2021: [113, 102, 99, 99, 86],
    2022: [116, 105, 110, 102, 89],
    2023: [124, 111, 118, 108, 94],
    2024: [129, 117, 125, 113, 99],
    2025: [138, 124, 134, 122, 106],
    2026: [145, 131, 140, 127, 110]
  };

  @action.bound
  async init(): Promise<void> {
    log("init");
    return this.fetchStaticData().then(this.setReady);
  }

  @computed get areasOfExpDictionary() {
    return this.areasOfExp.toRecord(
      (x) => x.Name,
      (x) => x
    );
  }

  @computed get professions(): string[] {
    if (!this.areasOfExp || this.areasOfExp.length === 0) {
      return [];
    }

    return this.areasOfExp.map((t) => toJS(t.Profession)).unique();
  }

  // bonliva care profession
  @computed get bcProfessions(): string[] {
    if (!this.areasOfExp || this.areasOfExp.length === 0) {
      return [];
    }

    return ["Läkare", "Psykolog"];
  }

  get staffingRoles() {
    if (!this.roles) {
      return [];
    }

    return this.roles.filter((r) => r.IsStaffing).map((r) => r.Name);
  }

  @computed get regionsByCountries() {
    return getRegionsAsOptions(this.regions);
  }

  @computed get regionsByCountriesWithBonlivaCare() {
    return getRegionsAsOptions(this.regionsWithBonlivaCare);
  }

  /**
   * All uniq tags available on all the teams
   */
  @computed get tags(): string[] {
    if (!this.teams || this.teams.length === 0) {
      return [];
    }

    return uniqFlatten(this.teams.map((t) => t.Tags));
  }

  /**
   * Returns all areas of expertise on all given teams
   */
  getAreasOfExpOnTeams(teams: string[]) {
    const expertiseNames = uniqFlatten(
      this.teams
        .filter((t) => teams.includes(t.Name))
        .map((t) => t.AreasOfExpertise.slice())
    );

    return this.areasOfExp.filter((x) => expertiseNames.includes(x.Name));
  }

  getSpecializationsOfExpertises(expNames: string[]): string[] {
    if (!Array.isArray(expNames) || expNames.length === 0) {
      return [];
    }

    const expertises = this.areasOfExp.filter((a) => expNames.includes(a.Name));

    return uniqFlatten(expertises.map((e) => e.Specializations));
  }

  getSpecializationsOfExpertise(expName: string, profession: string) {
    if (!expName || !profession) {
      return [];
    }

    const r = this.areasOfExp?.find(
      (x) => x.Profession === profession && x.Name === expName
    );

    if (!r) {
      return [];
    }

    return r.Specializations;
  }

  getAreasOfExpOnAreasOfExpAndProfessions(names: string[]) {
    return names.flatMap((name) => {
      const expertise = this.areasOfExp?.find((a) => a.Name === name);

      // if found - return an expertise
      if (expertise) {
        return [expertise];
      }

      // otherwise we are dealing with a profession
      return this.areasOfExp?.filter((a) => a.Profession === name);
    });
  }

  getAreaOfExpOnProfessions(professions: string[]) {
    return this.areasOfExp?.filter((a) => professions.includes(a.Profession));
  }

  getCountryRegions(country: string) {
    const c = this.allCountriesWorldwide.find((cc) => cc.name === country);
    return c.regions;
  }

  getProfessionsOnAreasofExp(areasOfExp: string[]) {
    if (isEmpty(areasOfExp)) {
      return [];
    }

    const _areasOfExp = this.areasOfExp.filter((x) => areasOfExp.includes(x.Name));

    return _areasOfExp.map((x) => x.Profession).unique();
  }

  /**
   * Returns a list of profession on all given teams
   */
  getProfessionsOnTeams(teams: string[]): string[] {
    const expertises = this.getAreasOfExpOnTeams(teams);
    return expertises.map((exp) => exp.Profession).unique();
  }

  getSystemsOnExpertises(expertiseNames: string[]) {
    const expertises = this.areasOfExp.filter((a) => expertiseNames.includes(a.Name));
    return uniqFlatten(expertises.map((e) => e.Systems));
  }

  getSystemsOnProfession(profession: string) {
    const aoes = this.areasOfExp.filter((x) => x.Profession === profession);
    return aoes[0].Systems; // systems are the same for each aoe in a profession
  }

  getMachinesOnProfession(profession: string) {
    const aoes = this.areasOfExp.filter((x) => x.Profession === profession);
    return aoes[0].Machines; // systems are the same for each aoe in a profession
  }

  getEducationsOnProfession(profession: string) {
    return this.areasOfExp?.find((x) => x.Profession === profession)?.Educations;
  }

  getMethodsOnProfession(profession: string) {
    return this.areasOfExp?.find((x) => x.Profession === profession)?.Methods;
  }

  fetchStaticData() {
    return FetchStaticData.func({}, { fetchPolicy: "network-only" }).then(({ data }) => {
      this.areasOfExp = Array.from(data.config.expertises);
      this.competitors = Array.from(data.config.competitors);
      this.customerGroups = Array.from(data.config.customerGroups);
      this.languages = Array.from(data.config.languages);
      this.roles = Array.from(data.config.roles);
      this.systems = uniqFlatten(data.config.expertises.map((e) => e.Systems));
      this.machines = uniqFlatten(data.config.expertises.map((e) => e.Machines));
      this.teams = Array.from(data.teams);
      this.titles = Array.from(data.config.titles);
      this.nationalProcurementTravelRegions = Array.from(
        data.config.nationalProcurementTravelRegions
      );
      this.blockBookingCreationForBemloCustomers =
        data.config.blockBookingCreationForBemloCustomers;

      data.config.countries.forEach((country) => {
        if (country.canWorkIn) {
          this.regions.set(
            country.name,
            country.regions.map((e) => e.name)
          );

          this.countries.push(country.name);
        }

        this.allCountriesWorldwide.push(country);
      });

      this.countries.push("Bonliva Care");

      this.regionsWithBonlivaCare = observable.map(this.regions);
      this.regionsWithBonlivaCare.set("Bonliva Care", this.regions.get("Sweden"));
    });
  }

  isStaffingRole = (role: string) => {
    return this.staffingRoles.includes(role);
  };
}

function getRegionsAsOptions(regions: ObservableMap<string, string[]>) {
  const result = Array.from(regions.entries())
    .map(([country, countryRegions]) => {
      return countryRegions.map((region) => ({
        label: region,
        value: `${country}:${region}`,
        parent: `${country}:*`
      }));
    })
    .selectMany((x) => x);

  Array.from(regions.keys()).forEach((country) => {
    result.push({
      parent: null,
      label: locale.getCountryName(country),
      value: `${country}:*`
    });
  });

  return result;
}

const mainLanguages = ["Swedish", "English", "Norwegian", "Danish", "Finnish"];

// 2400000, 1700000, malma, 75000, rest
export type BenefitTaxFactors = [number, number, number, number, number];
